/**
 * Vue Router — MaMarotte
 *
 * Route groups:
 *   /              → redirect to /accueil
 *   /login         → AuthView (no layout)
 *   /register      → AuthView (no layout)
 *   /accueil       → AccueilView   (AppShell layout)
 *   /compagnies    → CompagniesView (/troupes, son ancien nom, y renvoie)
 *   /pieces       → PiecesView
 *   /pieces/:id   → ScriptReaderView
 *   /productions   → ProductionsView
 *   /productions/:id → ProductionDetailView
 *   /preferences   → PreferencesView
 *   (inconnue)     → IntrouvableView
 */
import { createRouter, createWebHistory, type Router, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { titreOnglet } from '@/utils/titreOnglet'

// Layouts
const AppShell = () => import('@/components/layout/RAppShell.vue')

// Views — lazy loaded
const AccueilView = () => import('@/views/AccueilView.vue')
const CompagniesView = () => import('@/views/CompagniesView.vue')
const PiecesView = () => import('@/views/PiecesView.vue')
const ScriptEditorView = () => import('@/views/ScriptEditorView.vue')
const PieceDetailView = () => import('@/views/PieceDetailView.vue')
const ScriptReaderView = () => import('@/views/ScriptReaderView.vue')
const ConduiteView = () => import('@/views/ConduiteView.vue')
const ProductionsView = () => import('@/views/ProductionsView.vue')
const ProductionDetailView = () => import('@/views/ProductionDetailView.vue')
const PreferencesView = () => import('@/views/PreferencesView.vue')
const AideView = () => import('@/views/AideView.vue')
const EvenementDetailView = () => import('@/views/EvenementDetailView.vue')
const IntrouvableView = () => import('@/views/IntrouvableView.vue')

const routes: RouteRecordRaw[] = [
  // ── App (AppShell layout) ─────────────────────────────
  {
    path: '/',
    component: AppShell,
    meta: { requiresAuth: true },
    children: [
      // Index redirect lives here so the parent route record is never duplicated
      { path: '', redirect: '/accueil' },
      {
        path: 'accueil',
        name: 'accueil',
        component: AccueilView,
        meta: { title: 'Accueil' },
      },
      {
        path: 'compagnies',
        name: 'compagnies',
        component: CompagniesView,
        meta: { title: 'Mes compagnies' },
      },
      // Ancien nom de la page : favoris et appli installée
      { path: 'troupes', redirect: '/compagnies' },
      {
        path: 'pieces',
        name: 'pieces',
        component: PiecesView,
        meta: { title: 'Pièces' },
      },
      {
        path: 'pieces/:id',
        name: 'piece-detail',
        component: PieceDetailView,
        meta: { title: 'Pièce' },
      },
      {
        path: 'pieces/:id/script',
        name: 'editor',
        component: ScriptEditorView,
        meta: { title: 'Éditeur' },
      },
      {
        path: 'productions',
        name: 'productions',
        component: ProductionsView,
        meta: { title: 'Productions' },
      },
      {
        path: 'productions/:id',
        name: 'production-detail',
        component: ProductionDetailView,
        meta: { title: 'Production' },
      },
      {
        path: 'productions/:productionId/script',
        name: 'script-reader',
        component: ScriptReaderView,
        meta: { title: 'Texte' },
      },
      {
        path: 'productions/:productionId/conduite',
        name: 'conduite',
        component: ConduiteView,
        meta: { title: 'Conduite' },
      },
      {
        path: 'productions/:productionId/evenements/:evenementId',
        name: 'evenement-detail',
        component: EvenementDetailView,
        meta: { title: 'Événement' },
      },
      {
        path: 'preferences',
        name: 'preferences',
        component: PreferencesView,
        meta: { title: 'Préférences' },
      },
      {
        path: 'aide',
        name: 'aide',
        component: AideView,
        meta: { title: 'Aide' },
      },
      // Adresse inconnue : page introuvable dans la mise en page de l'app, sidebar comprise
      {
        path: ':pathMatch(.*)*',
        name: 'introuvable',
        component: IntrouvableView,
        meta: { title: 'Page introuvable' },
      },
    ],
  },
]

/**
 * À appeler après keycloak.init : createWebHistory lit window.location dès sa création, et la navigation
 * initiale remettrait dans l'URL le `#state=…&code=…` que keycloak-js vient d'effacer.
 */
export function createAppRouter(): Router {
  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
    // Une ancre absente au montage (#calendrier, chargé ensuite) laisse la vue défiler d'elle-même. Sur la même page, un
    // changement de query (le Lecteur qui efface `?action=` après avoir défilé jusqu'à la réplique) ne remonte pas en haut
    scrollBehavior: (to, from) => {
      if (to.hash) return { el: to.hash }
      return to.path === from.path ? false : { top: 0 }
    },
  })

  router.beforeEach((to) => {
    if (to.meta.requiresAuth) {
      const auth = useAuthStore()
      if (!auth.isAuthenticated) return '/'
    }
  })

  // Titre générique de la route ; les pages d'une pièce le remplacent par son titre une fois chargée (useTitreOnglet)
  router.afterEach((to) => {
    document.title = titreOnglet(to.meta.title as string | undefined)
  })

  return router
}
